// Z-Warehouse (Gary, 2026-09-24): does responsibility transfer at the warehouse door
// (COLLECTION_ONLY), or does CY UK also arrange the last-mile delivery
// (COLLECTION_AND_DELIVERY)?
//
// Two paths, in preference order:
//  1. Clean path: every selected item group has a delivery_method_id (picked from the
//     pricing_delivery_methods dropdown -- see components/DeliveryMethodSelect.tsx) -- look up
//     its default_fulfilment_type. A method marked ambiguous there (XDP_OVERSIZED: NULL) still
//     resolves via this path, just with no suggestion -- that's a clean "ask anyway" signal,
//     distinct from the free-text guessing below.
//  2. Historical/"Other" fallback: devanning_items.delivery_method is free text staff have typed
//     inconsistently for years (courier names, Chinese shorthand, even literal warehouse names)
//     -- a keyword suggestion, never a silent auto-apply. This path is permanent, not a
//     migration-era shim: the "Other" free-text option in the dropdown means new data can still
//     lack a clean delivery_method_id going forward.
// Either way, staff always confirms/overrides in the UI -- this only ever produces a suggestion.
export type FulfilmentType = 'COLLECTION_ONLY' | 'COLLECTION_AND_DELIVERY'

export interface FulfilmentSuggestion {
  type: FulfilmentType | null
  reason: string | null
}

export interface DeliveryMethodInput {
  deliveryMethodId: string | null
  deliveryMethodText: string | null
}

const COLLECTION_ONLY_KEYWORDS = ['dpd', 'ups', 'evri', 'pick', '自提', 'fba', 'amazon']
const COLLECTION_AND_DELIVERY_KEYWORDS = ['卡派', 'truck', 'ltl', '托盘']

function classifyOne(text: string): FulfilmentType | null {
  // Warehouse/address names ("伯明翰19号仓") describe a destination, not a delivery method --
  // never a reliable signal even if a substring happens to overlap a keyword below.
  if (/仓/.test(text)) return null
  const t = text.toLowerCase()
  if (COLLECTION_ONLY_KEYWORDS.some(k => t.includes(k))) return 'COLLECTION_ONLY'
  if (COLLECTION_AND_DELIVERY_KEYWORDS.some(k => t.includes(k))) return 'COLLECTION_AND_DELIVERY'
  return null
}

// `inputs` is every selected item group's own delivery_method_id/delivery_method -- pass all of
// them, not a pre-deduped/pre-guessed one. `methodLookup` is pricing_delivery_methods'
// id -> default_fulfilment_type (null entries included -- that's XDP_OVERSIZED's "ambiguous").
export function suggestFulfilmentType(
  inputs: DeliveryMethodInput[],
  methodLookup: Record<string, FulfilmentType | null>
): FulfilmentSuggestion {
  const nonEmpty = inputs.filter(i => i.deliveryMethodId || (i.deliveryMethodText || '').trim())
  if (nonEmpty.length === 0) return { type: null, reason: null }

  // Clean path only kicks in when EVERY selected item has a delivery_method_id -- a note mixing
  // clean picks with old-style/"Other" free text falls through to the heuristic below, which
  // also naturally covers "no clean id at all yet" for historical data.
  if (nonEmpty.every(i => i.deliveryMethodId)) {
    const ids = [...new Set(nonEmpty.map(i => i.deliveryMethodId as string))]
    if (ids.length > 1) {
      return { type: null, reason: `This note spans multiple delivery methods -- please choose manually.` }
    }
    const type = methodLookup[ids[0]] ?? null
    return {
      type,
      reason: type ? null : 'This delivery method needs manual confirmation (e.g. oversized items can go either way).',
    }
  }

  const texts = [...new Set(nonEmpty.map(i => (i.deliveryMethodText || '').trim()).filter(Boolean))]
  if (texts.length === 0) return { type: null, reason: null }
  if (texts.length > 1) {
    return { type: null, reason: `This note spans multiple delivery methods (${texts.join(', ')}) -- please choose manually.` }
  }
  const only = texts[0]
  const type = classifyOne(only)
  if (!type) return { type: null, reason: null }
  return { type, reason: `Suggested from this note's delivery method: "${only}"` }
}

// ── Note-level delivery method (Gary, 2026-09-29) ─────────────────────────────────────────
// One dropdown -- pricing_delivery_methods, the same vocabulary as quoting and per-item
// delivery_method_id -- decides BOTH the note's kind (Courier / Self-Pickup / Supplier Pickup
// title + Release To label + vehicle/driver fields) AND the fulfilment type default, so the two
// can no longer contradict each other (old UI: "Courier" + "Collection + Delivery").
export interface DeliveryMethodRow {
  id: string
  code: string
  name: string
  default_fulfilment_type: FulfilmentType | null
}

type NoteKind = 'COLLECTION_COURIER' | 'COLLECTION_SELF' | 'COLLECTION_SUPPLIER'

// The stored note_type column only distinguishes who physically collects; every method that
// isn't a customer/supplier pickup is "someone we release to" (courier, haulier, our own truck).
export function noteKindForMethodCode(code: string): NoteKind {
  if (code === 'SELF_PICKUP') return 'COLLECTION_SELF'
  if (code === 'SUPPLIER_PICKUP') return 'COLLECTION_SUPPLIER'
  return 'COLLECTION_COURIER'
}

export function methodCodeForNoteKind(kind: string): string | null {
  if (kind === 'COLLECTION_SELF') return 'SELF_PICKUP'
  if (kind === 'COLLECTION_SUPPLIER') return 'SUPPLIER_PICKUP'
  if (kind === 'COLLECTION_COURIER') return 'COURIER'
  return null
}

// The method every selected item agrees on, or null (mixed / any item without a clean pick).
export function commonMethodId(itemMethodIds: (string | null)[]): string | null {
  if (itemMethodIds.length === 0 || itemMethodIds.some(id => !id)) return null
  const ids = new Set(itemMethodIds as string[])
  return ids.size === 1 ? [...ids][0] : null
}

// Fulfilment default for a note: the chosen method's default_fulfilment_type when there is a
// method (NULL default = XDP_OVERSIZED = force a manual choice), otherwise the legacy
// free-text keyword suggestion from the items.
export function suggestFulfilmentForNote(
  methodId: string | null,
  methods: DeliveryMethodRow[],
  inputs: DeliveryMethodInput[],
): FulfilmentSuggestion {
  const method = methodId ? methods.find(m => m.id === methodId) : undefined
  if (method) {
    return method.default_fulfilment_type
      ? { type: method.default_fulfilment_type, reason: null }
      : { type: null, reason: `${method.name} can go either way -- please choose Collection Only or Collection + Delivery.` }
  }
  return suggestFulfilmentType(inputs, Object.fromEntries(methods.map(m => [m.id, m.default_fulfilment_type])))
}

// Distinct goods descriptions, comma-joined; more than 3 kinds collapses to "A, B, C +N more"
// so the Description cell never becomes a paragraph. Empty when nothing is known -- never a
// made-up placeholder, staff fills it in.
export function summariseDescriptions(descriptions: (string | null | undefined)[], max = 3): string {
  const distinct = [...new Set(descriptions.map(d => (d || '').trim()).filter(Boolean))]
  if (distinct.length <= max) return distinct.join(', ')
  return `${distinct.slice(0, max).join(', ')} +${distinct.length - max} more`
}
